import { useState } from "react";
import {
  C, Banner, labelStyle, inputStyle, btnPrimary, btnSecondary, btnDangerText,
} from "./ui.jsx";
import { actionsForStatus, ACTION_META } from "../constants.js";

/* Stable idempotency key per user action (reused across confirm retries so a
 * double-click / retry can never duplicate the transition). */
function genId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `bd-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/* Translate a backend error into a clear, non-technical message and decide how
 * to handle it. The backend state machine is authoritative — the frontend only
 * hides/shows for UX, so an "invalid transition" here means the row's status
 * changed underneath us (race); refresh and inform, don't expose internals. */
function interpretError(err, confirmLabel) {
  const code = err?.code;
  const msg = String(err?.message || "");
  if (code === "forbidden") {
    return { text: "You don't have permission to perform this action.", terminal: true, refresh: false };
  }
  if (code === "not_found") {
    return { text: "This birthday run no longer exists. The list has been refreshed.", terminal: true, refresh: true };
  }
  if (code === "conflict") {
    if (/in progress/i.test(msg)) {
      return { text: "This action is already being processed — please wait a moment.", terminal: false, refresh: false };
    }
    return {
      text: `This run's status changed, so “${confirmLabel}” is no longer available. The list has been refreshed.`,
      terminal: true, refresh: true,
    };
  }
  if (code === "validation_error") {
    return { text: msg || "Please check the form and try again.", terminal: false, refresh: false };
  }
  return { text: msg || "Something went wrong. Please try again.", terminal: false, refresh: false };
}

/* Gated action buttons + confirmation modal for a single birthday run.
 * Rendered only inside the expanded row detail, admin/hr only. Backend re-checks
 * permission + transition; this never sends, never calls n8n, never writes storage. */
export default function RowActions({ run, name, api, allowed, reload, reloadDetail, onNotice }) {
  const [active, setActive] = useState(null); // action key being confirmed
  const [reason, setReason] = useState("");
  const [idemKey, setIdemKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!allowed || !run) return null;
  const actions = actionsForStatus(run.status);
  if (actions.length === 0) {
    return (
      <div style={{ fontSize: 11.5, color: C.muted }}>
        No actions available for this status.
      </div>
    );
  }

  function open(action) {
    setActive(action);
    setReason("");
    setError(null);
    setIdemKey(genId());
  }

  function close() {
    if (submitting) return;
    setActive(null);
    setError(null);
  }

  const meta = active ? ACTION_META[active] : null;
  const reasonMode = meta?.reason; // "required" | "optional" | undefined
  const reasonMissing = reasonMode === "required" && !reason.trim();

  async function confirm() {
    if (!meta || submitting || reasonMissing) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.runAction({
        runId: run.id,
        action: active,
        reason: reason.trim() || undefined,
        idempotencyKey: idemKey,
      });
      // Replays / no-ops come back as success with changed:false — treat as safe.
      const label = ACTION_META[active]?.confirmLabel || active;
      onNotice?.(
        meta.tone === "warn" ? "warn" : "success",
        res?.changed === false
          ? `No change needed — this run is already “${res.status}”.`
          : `“${label}” applied — run is now “${res.status}”.`
      );
      setActive(null);
      await reload?.();
      reloadDetail?.(run.id);
    } catch (e) {
      const info = interpretError(e, meta.confirmLabel);
      if (info.terminal) {
        setActive(null);
        onNotice?.("danger", info.text);
        if (info.refresh) await reload?.();
      } else {
        setError(info.text);
      }
    }
    setSubmitting(false);
  }

  const triggerStyle = (kind) =>
    kind === "primary" ? btnPrimary : kind === "danger" ? btnDangerText : btnSecondary;

  return (
    <div>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>
        Actions
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {actions.map((a) => {
          const m = ACTION_META[a];
          return (
            <button key={a} onClick={() => open(a)} style={triggerStyle(m.kind)}>
              {m.label}
            </button>
          );
        })}
      </div>

      {meta && (
        <div
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", zIndex: 10002, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}
          onClick={close}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, boxShadow: "0 4px 24px rgba(0,0,0,.08)", maxWidth: 440, width: "100%", position: "relative", padding: 28 }}
          >
            <button onClick={close} disabled={submitting}
              style={{ position: "absolute", top: 16, right: 18, background: "none", border: "none", cursor: submitting ? "default" : "pointer", fontSize: 22, color: C.muted, lineHeight: 1 }}>
              ×
            </button>

            <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 6px", letterSpacing: "-.3px" }}>{meta.title}</h3>
            {name && (
              <div style={{ fontSize: 12.5, color: C.sub, marginBottom: 12 }}>
                {name}{run.id ? <span style={{ color: C.muted }}> · {String(run.id).slice(0, 8)}…</span> : null}
              </div>
            )}
            <p style={{ fontSize: 13, color: C.text, lineHeight: 1.5, margin: "0 0 14px" }}>{meta.body}</p>

            {reasonMode && (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>
                  Reason {reasonMode === "required" ? <span style={{ color: C.error }}>*</span> : <span style={{ color: C.muted, textTransform: "none" }}>(optional)</span>}
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder={reasonMode === "required" ? "Why is this run being skipped?" : "Optional note for the record"}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>
            )}

            {error && <div style={{ marginBottom: 14 }}><Banner tone="danger">{error}</Banner></div>}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={close} disabled={submitting} style={{ ...btnSecondary, opacity: submitting ? 0.6 : 1 }}>
                Cancel
              </button>
              <button onClick={confirm} disabled={submitting || reasonMissing}
                style={{ ...btnPrimary, opacity: submitting || reasonMissing ? 0.6 : 1, cursor: submitting || reasonMissing ? "default" : "pointer" }}>
                {submitting ? "Working…" : meta.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
